import { useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import { Tabs } from '@mantine/core'
import {
  usePmocAtivar,
  usePmocDetalhe,
  usePmocDuplicar,
  usePmocExportarPdf,
} from '../../hooks/usePmoc'
import { useOsList } from '../../hooks/useOs'
import { extractApiError, extractApiErrorList } from '../../lib/extractApiError'
import PlanosSection from './PlanosSection'
import PmocPlanosTab from './PmocPlanosTab'
import PmocOcorrenciasTab from './PmocOcorrenciasTab'
import PmocGerarOSTab from './PmocGerarOSTab'
import PmocArtsTab from './PmocArtsTab'
import PmocPlanoQaiTab from './PmocPlanoQaiTab'
import GerarPdfV2Modal from './GerarPdfV2Modal'
import styles from '../_shared/CrudPage.module.css'

const STATUS_COR: Record<string, { bg: string; color: string }> = {
  rascunho: { bg: '#FFF3E0', color: '#F57C00' },
  ativo: { bg: '#E8F5E9', color: '#388E3C' },
  expirado: { bg: '#F5F5F5', color: '#757575' },
  revogado: { bg: '#FFEBEE', color: '#C62828' },
  bloqueado: { bg: '#FFCDD2', color: '#B71C1C' },
}

export default function PmocDetalhePage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const detalhe = usePmocDetalhe(id)
  const ativar = usePmocAtivar(id)
  const duplicar = usePmocDuplicar()
  const pdf = usePmocExportarPdf(id)
  const osList = useOsList(id ? { pmoc: id, ordering: '-aberta_em' } : undefined)

  const [pdfV2Aberto, setPdfV2Aberto] = useState(false)
  const [aba, setAba] = useState<string>('visao-geral')

  if (detalhe.isLoading) return <div className={styles.loading}>Carregando PMOC...</div>
  if (detalhe.error || !detalhe.data) {
    return <div className={styles.empty}>PMOC não encontrado.</div>
  }
  const pmoc = detalhe.data
  const cor = STATUS_COR[pmoc.status] ?? STATUS_COR.rascunho

  async function handleAtivar() {
    if (!confirm(`Ativar PMOC v${pmoc.versao}? Após ativo, edição será bloqueada.`)) return
    try {
      await ativar.mutateAsync()
      notifications.show({ color: 'green', message: 'PMOC ativado.' })
    } catch (err) {
      const errors = extractApiErrorList(err)
      if (errors.length > 1) {
        notifications.show({
          color: 'red',
          title: `Falha ao ativar — ${errors.length} problemas`,
          message: errors.slice(0, 5).join(' • '),
          autoClose: 10000,
        })
      } else {
        notifications.show({
          color: 'red',
          title: 'Falha ao ativar',
          message: extractApiError(err, 'Operação falhou.'),
        })
      }
    }
  }

  async function handleDuplicar() {
    try {
      const novo = await duplicar.mutateAsync(id)
      notifications.show({
        color: 'green',
        message: `Cópia criada como PMOC v${novo.versao} (rascunho).`,
      })
      navigate(`/pmoc/${novo.id}`)
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha ao duplicar',
        message: extractApiError(err, 'Operação falhou.'),
      })
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <button
            className={styles.backBtn}
            type="button"
            onClick={() => navigate('/pmoc')}
          >
            ← Voltar
          </button>
          <h1 className={styles.title}>
            PMOC v{pmoc.versao}
            {pmoc.titulo && <span style={{ fontWeight: 400 }}> — {pmoc.titulo}</span>}
          </h1>
          <span
            data-testid="pmoc-status"
            style={{
              display: 'inline-block',
              padding: '0.2rem 0.6rem',
              borderRadius: '4px',
              backgroundColor: cor.bg,
              color: cor.color,
              fontSize: '0.85rem',
              marginTop: '0.5rem',
            }}
          >
            {pmoc.status}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {pmoc.status === 'rascunho' && (
            <>
              <Link
                to={`/pmoc/${id}/editar`}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'white',
                  color: '#1565C0',
                  border: '1px solid #1565C0',
                  borderRadius: 4,
                  textDecoration: 'none',
                  fontWeight: 600,
                }}
                data-testid="btn-pmoc-editar"
              >
                ✏ Editar
              </Link>
              <button
                type="button"
                data-testid="btn-pmoc-ativar"
                onClick={handleAtivar}
                disabled={ativar.isPending}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#2E7D32',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {ativar.isPending ? 'Ativando...' : '✓ Ativar'}
              </button>
            </>
          )}
          <button
            type="button"
            data-testid="btn-pmoc-duplicar"
            onClick={handleDuplicar}
            disabled={duplicar.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'white',
              color: '#1565C0',
              border: '1px solid #1565C0',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {duplicar.isPending ? 'Duplicando...' : '⎘ Duplicar'}
          </button>
          <button
            type="button"
            onClick={() => pdf.mutate()}
            disabled={pdf.isPending}
            title="PDF v1 (legado)"
            style={{
              padding: '0.5rem 1rem',
              background: 'white',
              color: '#666',
              border: '1px solid #888',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            {pdf.isPending ? 'Gerando...' : '📄 PDF v1'}
          </button>
          <button
            type="button"
            onClick={() => setPdfV2Aberto(true)}
            data-testid="btn-pmoc-pdf-v2"
            style={{
              padding: '0.5rem 1rem',
              background: '#1565C0',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            📄 PDF v2
          </button>
        </div>
      </header>

      <Tabs value={aba} onChange={(v) => v && setAba(v)} mt="md">
        <Tabs.List>
          <Tabs.Tab value="visao-geral">Visão geral</Tabs.Tab>
          <Tabs.Tab value="planos">Planos e atividades</Tabs.Tab>
          <Tabs.Tab value="ocorrencias">Ocorrências</Tabs.Tab>
          <Tabs.Tab value="gerar-os">Gerar OS</Tabs.Tab>
          <Tabs.Tab value="arts">ARTs</Tabs.Tab>
          <Tabs.Tab value="qai">Análise do ar</Tabs.Tab>
          <Tabs.Tab value="legado">Planos legados</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="visao-geral" pt="md">
          <section
            style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: 8,
              marginBottom: '1rem',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
          >
            <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Identificação</h2>
            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem' }}>
              <dt style={{ color: '#666' }}>Cliente:</dt>
              <dd style={{ margin: 0 }}>
                {pmoc.cliente_nome ?? pmoc.site_nome ?? pmoc.cliente ?? pmoc.site ?? '—'}
              </dd>
              <dt style={{ color: '#666' }}>Vigência:</dt>
              <dd style={{ margin: 0 }}>
                {pmoc.vigencia_inicio ?? '—'}
                {pmoc.vigencia_fim ? ` → ${pmoc.vigencia_fim}` : ' (indeterminada)'}
              </dd>
              <dt style={{ color: '#666' }}>Norma:</dt>
              <dd style={{ margin: 0 }}>{pmoc.referencia_normativa ?? '—'}</dd>
              {pmoc.numero_art && (
                <>
                  <dt style={{ color: '#666' }}>ART/RRT (legado):</dt>
                  <dd style={{ margin: 0 }}>{pmoc.numero_art}</dd>
                </>
              )}
            </dl>
          </section>

          <section
            style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: 8,
              marginBottom: '1rem',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
          >
            <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Atores</h2>
            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem' }}>
              <dt style={{ color: '#666' }}>Planejador:</dt>
              <dd style={{ margin: 0 }}>{pmoc.planejador_nome || pmoc.elaboradora_nome || '—'}</dd>
              <dt style={{ color: '#666' }}>RT Planejamento:</dt>
              <dd style={{ margin: 0 }}>{pmoc.rt_planejamento_str || '—'}</dd>
              <dt style={{ color: '#666' }}>Executor:</dt>
              <dd style={{ margin: 0 }}>{pmoc.executor_nome || pmoc.executora_nome || '—'}</dd>
              <dt style={{ color: '#666' }}>RT Execução:</dt>
              <dd style={{ margin: 0 }}>{pmoc.rt_execucao_str || '—'}</dd>
              <dt style={{ color: '#666' }}>Laboratório QAI:</dt>
              <dd style={{ margin: 0 }}>
                {pmoc.laboratorio_nome || pmoc.executora_qai_nome || '— (sem QAI separada)'}
              </dd>
              <dt style={{ color: '#666' }}>RT Laboratório:</dt>
              <dd style={{ margin: 0 }}>{pmoc.rt_laboratorio_str || '—'}</dd>
            </dl>
          </section>

          <section
            style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: 8,
              marginBottom: '1rem',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
          >
            <h2 style={{ marginTop: 0, fontSize: '1rem' }}>
              Equipamentos vinculados ({pmoc.equipamentos?.length ?? 0})
            </h2>
            {pmoc.equipamentos && pmoc.equipamentos.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                {pmoc.equipamentos.map((eq) => (
                  <li key={eq.id}>
                    {eq.marca} {eq.modelo} ({eq.tipo})
                    {' · '}
                    <Link to={`/equipamentos/${eq.id}?pmoc=${id}`} style={{ fontSize: '0.85rem' }}>
                      Planos compatíveis →
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: '#666', margin: 0 }}>Nenhum equipamento vinculado.</p>
            )}
          </section>

          <section
            style={{
              background: 'white',
              padding: '1.25rem',
              borderRadius: 8,
              marginBottom: '1rem',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
            data-testid="pmoc-os-section"
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '1rem' }}>
                Ordens de serviço deste PMOC ({osList.data?.count ?? 0})
              </h2>
              <Link
                to={`/ordens-servico/novo?pmoc=${id}`}
                style={{
                  padding: '0.4rem 0.8rem',
                  background: '#1565C0',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: 4,
                  fontSize: '0.85rem',
                }}
              >
                + Nova OS
              </Link>
            </div>
            {osList.isLoading ? (
              <p style={{ color: '#666', margin: 0 }}>Carregando...</p>
            ) : (osList.data?.results.length ?? 0) === 0 ? (
              <p style={{ color: '#666', margin: 0 }}>
                Nenhuma OS associada a este PMOC ainda. Use a aba "Gerar OS" para
                gerar a partir das ocorrências.
              </p>
            ) : (
              <table
                style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}
              >
                <thead>
                  <tr style={{ background: '#F5F5F5' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Número</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Tipo</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Prioridade</th>
                    <th style={{ padding: '0.5rem' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {osList.data?.results.slice(0, 10).map((os) => (
                    <tr key={os.id} style={{ borderTop: '1px solid #EEE' }}>
                      <td style={{ padding: '0.5rem', fontWeight: 600 }}>
                        OS #{os.numero}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        {os.tipo?.replace(/_/g, ' ') ?? '—'}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        {os.status?.replace('_', ' ') ?? '—'}
                      </td>
                      <td style={{ padding: '0.5rem' }}>{os.prioridade}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                        <Link
                          to={`/ordens-servico/${os.id}`}
                          style={{ color: '#1565C0', fontSize: '0.85rem' }}
                        >
                          Detalhes →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(osList.data?.count ?? 0) > 10 && (
              <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                Mostrando 10 de {osList.data?.count}.{' '}
                <Link to={`/ordens-servico?pmoc=${id}`}>Ver todas →</Link>
              </p>
            )}
          </section>

          {pmoc.observacoes_tecnicas && (
            <section
              style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: 8,
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              }}
            >
              <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Observações técnicas</h2>
              <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{pmoc.observacoes_tecnicas}</p>
            </section>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="planos" pt="md">
          <PmocPlanosTab pmoc={pmoc} />
        </Tabs.Panel>

        <Tabs.Panel value="ocorrencias" pt="md">
          <PmocOcorrenciasTab pmoc={pmoc} />
        </Tabs.Panel>

        <Tabs.Panel value="gerar-os" pt="md">
          <PmocGerarOSTab pmoc={pmoc} />
        </Tabs.Panel>

        <Tabs.Panel value="arts" pt="md">
          <PmocArtsTab pmoc={pmoc} />
        </Tabs.Panel>

        <Tabs.Panel value="qai" pt="md">
          <PmocPlanoQaiTab pmoc={pmoc} />
        </Tabs.Panel>

        <Tabs.Panel value="legado" pt="md">
          <PlanosSection pmoc={pmoc} />
        </Tabs.Panel>
      </Tabs>

      <GerarPdfV2Modal
        open={pdfV2Aberto}
        onClose={() => setPdfV2Aberto(false)}
        pmocId={id}
      />
    </div>
  )
}
